import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { defaultAssignmentsAPI, usersAPI } from '../../services/api';
import { formatDisplayName } from '../../utils/displayName';

interface DefaultAssignmentsModalProps {
  slotId: string;
  slotTitle: string;
  slotCapacity: number;
  onClose: () => void;
}

const DefaultAssignmentsModal: React.FC<DefaultAssignmentsModalProps> = ({
  slotId,
  slotTitle,
  slotCapacity,
  onClose,
}) => {
  const [selectedUserId, setSelectedUserId] = useState<string>('');
  const queryClient = useQueryClient();

  const { data: defaultAssignments = [], isLoading: loadingAssignments } = useQuery({
    queryKey: ['defaultAssignments', slotId],
    queryFn: () => defaultAssignmentsAPI.getSlotDefaultAssignments(slotId),
  });

  const { data: users = [], isLoading: loadingUsers } = useQuery({
    queryKey: ['users'],
    queryFn: usersAPI.getUsers,
  });

  const createMutation = useMutation({
    mutationFn: ({ userId, trainingSlotId }: { userId: string; trainingSlotId: string }) =>
      defaultAssignmentsAPI.createDefaultAssignment(userId, trainingSlotId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['defaultAssignments'] });
      setSelectedUserId('');
      alert('Default assignment added successfully!');
    },
    onError: (error: any) => {
      alert(error.response?.data?.error || 'Failed to add default assignment');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (assignmentId: string) =>
      defaultAssignmentsAPI.deleteDefaultAssignment(assignmentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['defaultAssignments'] });
      queryClient.invalidateQueries({ queryKey: ['slots'] });
      alert('Default assignment removed successfully!');
    },
  });

  const handleAdd = () => {
    if (!selectedUserId) {
      alert('Please select a user');
      return;
    }

    // Check if assignment already exists
    const exists = defaultAssignments.some((da) => da.userId === selectedUserId);
    if (exists) {
      alert('This user is already assigned as default');
      return;
    }

    // Check capacity
    if (defaultAssignments.length >= slotCapacity) {
      alert(`Cannot add more default assignments. Event capacity is ${slotCapacity} and there are already ${defaultAssignments.length} default assignments.`);
      return;
    }

    createMutation.mutate({ userId: selectedUserId, trainingSlotId: slotId });
  };

  // Filter out users that already have default assignments
  const availableUsers = users.filter(
    (user) => !defaultAssignments.some((da) => da.userId === user.id)
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-2xl font-semibold text-gray-800">
              Default Assignments: {slotTitle}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 text-2xl font-bold"
            >
              ×
            </button>
          </div>

          <p className="text-gray-600 mb-4">
            Users with default assignments will be automatically booked for this event. They can
            cancel individual instances if needed.
          </p>
          
          <div className="mb-6 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm text-gray-700">
              <span className="font-semibold">Event Capacity:</span> {slotCapacity} | 
              <span className="font-semibold ml-2">Current Default Assignments:</span> {defaultAssignments.length} | 
              <span className="font-semibold ml-2">Available:</span> {Math.max(0, slotCapacity - defaultAssignments.length)}
            </p>
            {defaultAssignments.length >= slotCapacity && (
              <p className="text-sm text-red-600 font-semibold mt-1">
                ⚠️ Capacity reached! Cannot add more default assignments.
              </p>
            )}
          </div>

          {/* Add Default Assignment */}
          <div className="mb-6 p-4 bg-gray-50 rounded-lg">
            <h3 className="text-lg font-semibold mb-3">Add Default Assignment</h3>
            <div className="flex gap-2">
              <select
                value={selectedUserId}
                onChange={(e) => setSelectedUserId(e.target.value)}
                className="flex-1 border rounded px-3 py-2"
                disabled={loadingUsers || availableUsers.length === 0 || defaultAssignments.length >= slotCapacity}
              >
                <option value="">
                  {loadingUsers
                    ? 'Loading users...'
                    : availableUsers.length === 0
                    ? 'All users already assigned'
                    : 'Select a user'}
                </option>
                {availableUsers.map((user) => (
                  <option key={user.id} value={user.id}>
                    {formatDisplayName(user)} {user.role === 'ADMIN' && '(Admin)'}
                  </option>
                ))}
              </select>
              <button
                onClick={handleAdd}
                disabled={!selectedUserId || createMutation.isPending || defaultAssignments.length >= slotCapacity}
                className="bg-primary-600 text-white px-4 py-2 rounded hover:bg-primary-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                {createMutation.isPending ? 'Adding...' : 'Add'}
              </button>
            </div>
          </div>

          {/* List of Default Assignments */}
          <div>
            <h3 className="text-lg font-semibold mb-3">
              Current Default Assignments ({defaultAssignments.length})
            </h3>
            {loadingAssignments ? (
              <p className="text-gray-500">Loading...</p>
            ) : defaultAssignments.length === 0 ? (
              <p className="text-gray-500">No default assignments yet.</p>
            ) : (
              <div className="space-y-2">
                {defaultAssignments.map((assignment) => (
                  <div
                    key={assignment.id}
                    className="flex justify-between items-center p-3 bg-white border rounded-lg"
                  >
                    <div>
                      <span className="font-medium">{formatDisplayName(assignment.user)}</span>
                      {assignment.user.role === 'ADMIN' && (
                        <span className="ml-2 text-xs bg-gray-200 px-2 py-1 rounded">
                          Admin
                        </span>
                      )}
                      <p className="text-xs text-gray-500">
                        Assigned on {new Date(assignment.createdAt).toLocaleDateString()}
                      </p>
                    </div>
                    <button
                      onClick={() => {
                        if (
                          window.confirm(
                            `Remove default assignment for ${formatDisplayName(assignment.user)}? Existing bookings will remain, but no new bookings will be auto-created.`
                          )
                        ) {
                          deleteMutation.mutate(assignment.id);
                        }
                      }}
                      disabled={deleteMutation.isPending}
                      className="text-accent-600 hover:text-accent-700 font-medium disabled:opacity-50"
                    >
                      Remove
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="mt-6 flex justify-end">
            <button
              onClick={onClose}
              className="bg-gray-200 text-gray-800 px-4 py-2 rounded hover:bg-gray-300"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DefaultAssignmentsModal;

